import { DateTime } from 'luxon'
import { type RRuleOptions } from './types'
import { RRule, Frequency, type Weekday } from 'rrule'

export class RRuleParser {
  static parse(rrule: string): RRuleOptions {
    const options: RRuleOptions = {
      freq: Frequency.DAILY, // default
      interval: 1,
    }

    rrule.split(';').forEach((piece) => {
      const [key, val] = piece.split('=')
      switch (key) {
        case 'FREQ':
          options.freq = RRuleParser.frequencyToRRuleJSFormat(val)
          break
        case 'INTERVAL':
          options.interval = parseInt(val, 10)
          break
        case 'COUNT':
          options.count = parseInt(val, 10)
          break
        case 'UNTIL':
          options.until = DateTime.fromFormat(
            val,
            "yyyyMMdd'T'HHmmss",
          ).toJSDate()
          break
        case 'BYDAY':
          options.byDay = RRuleParser.byDayToRRuleJSFormat(val)
          break
        case 'EXDATE':
          options.exDates = val.split(',').map((d) => {
            if (d.includes('T')) {
              return DateTime.fromFormat(d, "yyyyMMdd'T'HHmmss")
            } else {
              return DateTime.fromFormat(d, 'yyyyMMdd')
            }
          })
          console.log('Parsed EXDATE:', options.exDates)
          break
        case 'DTEND':
          options.dtEnd = DateTime.fromFormat(val, "yyyyMMdd'T'HHmmss")
          break
      }
    })

    return options
  }

  static byDayToRRuleJSFormat(byDay: string): Weekday[] {
    const weekdays: Weekday[] = []
    const days = byDay.split(',')
    days.forEach((day) => {
      // Check if day has a number prefix (e.g., '1MO', '-1FR')
      const match = day.match(/^(-?\d+)?(MO|TU|WE|TH|FR|SA|SU)$/)

      if (!match) {
        throw new Error(`Invalid day format: ${day}`)
      }

      const nth = match[1] ? parseInt(match[1], 10) : null
      const weekday = match[2]

      switch (weekday) {
        case 'MO':
          weekdays.push(nth !== null ? RRule.MO.nth(nth) : RRule.MO)
          break
        case 'TU':
          weekdays.push(nth !== null ? RRule.TU.nth(nth) : RRule.TU)
          break
        case 'WE':
          weekdays.push(nth !== null ? RRule.WE.nth(nth) : RRule.WE)
          break
        case 'TH':
          weekdays.push(nth !== null ? RRule.TH.nth(nth) : RRule.TH)
          break
        case 'FR':
          weekdays.push(nth !== null ? RRule.FR.nth(nth) : RRule.FR)
          break
        case 'SA':
          weekdays.push(nth !== null ? RRule.SA.nth(nth) : RRule.SA)
          break
        case 'SU':
          weekdays.push(nth !== null ? RRule.SU.nth(nth) : RRule.SU)
          break
        default:
          throw new Error(`Unknown day: ${day}`)
      }
    })
    return weekdays
  }

  static frequencyToRRuleJSFormat(frequencyString: string): Frequency {
    switch (frequencyString) {
      case 'YEARLY':
        return Frequency.YEARLY
      case 'MONTHLY':
        return Frequency.MONTHLY
      case 'WEEKLY':
        return Frequency.WEEKLY
      case 'DAILY':
        return Frequency.DAILY
      case 'HOURLY':
        return Frequency.HOURLY
      case 'MINUTELY':
        return Frequency.MINUTELY
      case 'SECONDLY':
        return Frequency.SECONDLY
      default:
        throw new Error(`Unknown frequency: ${frequencyString}`)
    }
  }
}
